"use client";
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Input,
  Button,
  Table,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";
import TableItem from "./TableItem";
import UserDetailsModal from "./UserDetailsModal";
import { useSelector } from "react-redux";
import Breadcrumbs from "../../components/Common/Breadcrumb";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    pending: 0,
  });

  const token = useSelector((state) => state.login.token);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.get("/admin/users");

      if (response.data.success) {
        setUsers(response.data.data);
        calculateStats(response.data.data);
      } else {
        toast.error("Failed to fetch users");
      }
    } catch (error) {
      console.error("Fetch users error:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (usersData) => {
    const stats = {
      total: usersData.length,
      active: usersData.filter((user) => user.status === "active").length,
      inactive: usersData.filter((user) => user.status === "inactive").length,
      pending: usersData.filter((user) => user.state === 0).length,
    };
    setStats(stats);
  };

  const handleShow = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        const response = await Api.delete(`/admin/users/${userId}`);

        if (response.data.success) {
          toast.success("User deleted successfully");
          fetchUsers(); // Refresh the list
        } else {
          toast.error(response.data.message || "Failed to delete user");
        }
      } catch (error) {
        console.error("Delete user error:", error);
        toast.error("Failed to delete user");
      }
    }
  };

  const handleQuickActivate = async (userId) => {
    try {
      const response = await Api.put(`/admin/users/${userId}/approve`);

      if (response.data.success) {
        toast.success("User approved and activated successfully");
        fetchUsers(); // Refresh the list
      } else {
        toast.error(response.data.message || "Failed to approve user");
      }
    } catch (error) {
      console.error("Quick activate error:", error);
      toast.error("Failed to approve user");
    }
  };

  const handleStatusUpdate = (userId, newStatus, newState) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === userId
          ? { ...user, status: newStatus, state: newState }
          : user
      )
    );
    calculateStats(
      users.map((user) =>
        user.id === userId
          ? { ...user, status: newStatus, state: newState }
          : user
      )
    );
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone?.includes(searchTerm)
  );

  if (loading) {
    return (
      <Container fluid>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "400px" }}
        >
          <Spinner color="primary" size="lg" />
        </div>
      </Container>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title="Users Management"
            breadcrumbItem="Users Management"
          />

          {/* Statistics Cards */}
          <Row>
            <Col xl={3} md={6}>
              <Card className="card-animate">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1 overflow-hidden">
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                        Total Users
                      </p>
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between mt-4">
                    <div>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                        <span className="counter-value">{stats.total}</span>
                      </h4>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-success-subtle rounded fs-3">
                        <i className="bx bx-user text-success"></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={3} md={6}>
              <Card className="card-animate">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1 overflow-hidden">
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                        Active Users
                      </p>
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between mt-4">
                    <div>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                        <span className="counter-value">{stats.active}</span>
                      </h4>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-info-subtle rounded fs-3">
                        <i className="bx bx-check-circle text-info"></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={3} md={6}>
              <Card className="card-animate">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1 overflow-hidden">
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                        Inactive Users
                      </p>
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between mt-4">
                    <div>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                        <span className="counter-value">{stats.inactive}</span>
                      </h4>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-warning-subtle rounded fs-3">
                        <i className="bx bx-x-circle text-warning"></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={3} md={6}>
              <Card className="card-animate">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1 overflow-hidden">
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                        Pending Approval
                      </p>
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between mt-4">
                    <div>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                        <span className="counter-value">{stats.pending}</span>
                      </h4>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-danger-subtle rounded fs-3">
                        <i className="bx bx-time text-danger"></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Users Table */}
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">Users List</h4>
                </CardHeader>

                <CardBody>
                  <div className="listjs-table" id="userList">
                    <Row className="g-4 mb-3">
                      <Col className="col-sm-auto">
                        <div>
                          <Button
                            color="success"
                            className="add-btn"
                            onClick={fetchUsers}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>
                            Refresh
                          </Button>
                        </div>
                      </Col>
                      <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end">
                          <div className="search-box ms-2">
                            <Input
                              type="text"
                              className="form-control search"
                              placeholder="Search users..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <i className="ri-search-line search-icon"></i>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      <Table
                        className="align-middle table-nowrap"
                        id="userTable"
                      >
                        <thead className="table-light">
                          <tr>
                            <th className="sort" data-sort="user_id">
                              ID
                            </th>
                            <th className="sort" data-sort="user_name">
                              User
                            </th>
                            <th className="sort" data-sort="user_type">
                              Type
                            </th>
                            <th className="sort" data-sort="user_status">
                              Status
                            </th>
                            <th className="sort" data-sort="phone">
                              Phone
                            </th>
                            <th className="sort" data-sort="social_media">
                              Social Media
                            </th>
                            <th className="sort" data-sort="date">
                              Joining Date
                            </th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody className="list form-check-all">
                          {filteredUsers.map((user) => (
                            <TableItem
                              key={user.id}
                              user={user}
                              handleShow={handleShow}
                              handleDelete={handleDelete}
                              onQuickActivate={handleQuickActivate}
                            />
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {filteredUsers.length === 0 && (
                      <div className="noresult">
                        <div className="text-center">
                          <lord-icon
                            src="https://cdn.lordicon.com/msoeawqm.json"
                            trigger="loop"
                            colors="primary:#121331,secondary:#08a88a"
                            style={{ width: "75px", height: "75px" }}
                          ></lord-icon>
                          <h5 className="mt-2">Sorry! No Result Found</h5>
                          <p className="text-muted mb-0">
                            We've searched through all users but couldn't find
                            any matching records.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* User Details Modal */}
          <UserDetailsModal
            modal={showModal}
            toggle={() => setShowModal(!showModal)}
            user={selectedUser}
            onStatusUpdate={handleStatusUpdate}
          />
        </Container>
      </div>
    </React.Fragment>
  );
}
